import { createContext, useContext, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from './cn.js';

/**
 * A medida da página, e por que ela é uma escolha de três valores.
 *
 * ## O defeito que isto conserta
 *
 * Cada tela escolhia a própria largura máxima à mão, no `className` da div
 * raiz. Medido no navegador em 01/09/2026, a mesma interna entregava **seis
 * larguras diferentes** em doze rotas: 672px em Preferências, 768px em Início e
 * Grupos, 896px em Usuários e Ranking, 1024px em Demandas e Comissões, 1096px
 * (sem teto) na Mesa e no Quadro. Navegar Início → Demandas → Usuários → Grupos
 * fazia a coluna de conteúdo pular 768 → 1024 → 896 → 768 a cada clique.
 *
 * Nenhum teste pegava, porque cada tela isolada estava certa. O que estava
 * errado era o conjunto — e conjunto ninguém revisa arquivo por arquivo.
 *
 * ## A regra, e ela é sobre o conteúdo
 *
 * Não é "escolha um número que caiba". É o que a tela mostra que decide:
 *
 * - `leitura` — formulário, detalhe, prosa. Uma coluna que se lê de cima a
 *   baixo, e que fica ilegível quando a linha passa de ~100 caracteres.
 * - `cheia` — tabela, mesa, quadro, relatório. O conteúdo é feito de colunas, e
 *   a largura é o que permite pôr as colunas lado a lado em vez de empilhá-las.
 *
 * **Eram três até 06/09**, com `tabela` em `max-w-5xl` (1024px) entre os dois.
 * O desenho de 05/09 tem duas medidas e não três: o formulário em 920px e todo
 * o resto ocupando a área útil inteira, que o `Layout` já limita em 1560px. A
 * mesa de demandas em 1024px era o que fazia a tela usar menos da metade de um
 * monitor de trabalho — visto lado a lado com a referência em 06/09.
 *
 * Dois degraus e não seis porque um degrau só se justifica se alguém consegue
 * dizer, sem olhar o código, qual tela cai nele. `tabela` não passava nesse
 * teste: ninguém sabia dizer por que a mesa de cobrança era `cheia` e a de
 * demandas não.
 *
 * `Page` também fixa o ritmo vertical entre os blocos da tela (`gap-5`) — o
 * outro valor que estava escrito à mão em 47 arquivos, com três telas em
 * `gap-6` sem que nada explicasse a diferença.
 */
export type Measure = 'leitura' | 'cheia';

/**
 * A classe de cada medida — exportada porque a MOLDURA precisa da mesma.
 *
 * O portal desenha o `<h1>` e as ações da tela dentro do bloco do cabeçalho
 * (ver `PageHeaderSlotProvider`), e o bloco é da moldura, não da tela. Sem
 * dividir daqui a mesma medida, a coluna do título e a do conteúdo tinham
 * larguras diferentes: medido a 1280px, "Abrir demanda" terminava 56px à
 * direita do último cartão que ele abre.
 *
 * Repetir `max-w-[57.5rem]` no `Layout` seria a segunda cópia do número — o
 * defeito que este arquivo inteiro existe para tratar.
 *
 * As chaves são os VALORES de `Measure`, não nomes nossos — o mapa é indexado
 * pela união literal, e traduzi-las quebra a indexação. Daí o `@valor`, que
 * precisa ficar colado na declaração: a marca alcança oito linhas, e este
 * docblock é maior que isso.
 */
// @valor
export const MEASURES: Record<Measure, string> = {
  /* 920px — a medida do formulário no desenho de 05/09. Eram 768. */
  leitura: 'max-w-[57.5rem]',
  /* Sem teto próprio: quem limita é o `main` do `Layout`, em 1560px. */
  cheia: '',
};

export function Page({
  measure = 'leitura',
  children,
  className,
}: {
  measure?: Measure;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col gap-5', MEASURES[measure], className)}>
      {children}
    </div>
  );
}

/**
 * O LUGAR onde o cabeçalho da tela é desenhado, quando ele não é aqui.
 *
 * Vazio por padrão, e é isso que mantém a interna intacta: sem provedor,
 * `PageHeader` desenha onde foi escrito, exatamente como antes.
 *
 * O portal provê um nó que vive DENTRO do bloco tingido da moldura, e aí todo
 * `<h1>` de tela passa a nascer lá — que é o que o `Evogard Portal v4` desenha e
 * o que o dono pediu: "quero o título embaixo do mesmo cabeçalho escuro da barra
 * de cima".
 *
 * ## Por que um portal, e não uma prop em cada tela
 *
 * São onze telas no portal e trinta e nove na interna. Uma prop faria cada uma
 * declarar onde o próprio título mora — e a resposta é sempre a mesma, e é da
 * MOLDURA, não da tela. A tela continua dizendo só o que o título diz; quem
 * decide o lugar é quem desenha a moldura. Uma tela nova entra no padrão sem
 * saber que ele existe, que é o teste de um padrão que se sustenta.
 *
 * `surface` anda junto porque cor de texto se decide pelo fundo REAL — a mesma
 * lição que `Wordmark`, `Metric` e este componente já aprenderam cada um por
 * conta própria. Quem oferece o lugar sabe de que cor ele é; a tela não.
 */
const PageHeaderSlot = createContext<{
  node: HTMLElement | null;
  surface: 'superficie' | 'chrome';
}>({ node: null, surface: 'superficie' });

export function PageHeaderSlotProvider({
  node,
  surface = 'superficie',
  children,
}: {
  node: HTMLElement | null;
  surface?: 'superficie' | 'chrome';
  children: ReactNode;
}) {
  return (
    <PageHeaderSlot.Provider value={{ node, surface }}>{children}</PageHeaderSlot.Provider>
  );
}

/**
 * O cabeçalho da tela: o título, a frase que o explica e as ações da página.
 *
 * ## Por que isto virou componente
 *
 * É a composição mais repetida do produto — 50 ocorrências nas duas SPAs — e
 * era escrita à mão em **nove dialetos** de `className`. Alguns com
 * `font-titulo`, outros sem (redundante nos dois casos: o `theme.css` já põe
 * a fonte de título em `h1`); a descrição com `mt-0.5`, `mt-1` ou `mt-3`; três telas da
 * plataforma em `text-2xl`, que o `DESIGN.md` reserva para a marca.
 *
 * ## E o que ele conserta de verdade
 *
 * `tracking-tight` aparecia em **todos** os 50. Ele é um utilitário do
 * Tailwind (-0.025em) e ganha da camada base, onde o `theme.css` declara
 * `h1 { letter-spacing: -0.03em }`. Ou seja: o valor desenhado no arquivo de
 * tokens nunca chegou a nenhuma tela — cada `h1` da plataforma o sobrescrevia,
 * por hábito copiado. Aqui não há `tracking`, e o token volta a valer.
 *
 * Não recebe `nivel`: a tela tem **um** `h1`, e é este.
 */
export function PageHeader({
  title,
  actions,
  aside,
  back,
  eyebrow,
  children,
  surface,
}: {
  title: ReactNode;
  /** Os botões da página, à direita. */
  actions?: ReactNode;
  /** O que anda junto do título — um selo de situação, um código. */
  aside?: ReactNode;
  /** O link de volta, acima do título. Quem navega é a SPA; aqui é só o lugar. */
  back?: ReactNode;
  /**
   * A fileira que vem ANTES do título — código, situação, prioridade.
   *
   * Diferente de `aside`, que anda ao lado do título e cabe um selo. Quando
   * são três ou mais, ao lado eles empurram o título para a segunda linha em
   * qualquer tela estreita, e a coisa mais importante da página passa a
   * disputar espaço com a etiqueta dela. Acima, cada um fica legível e o
   * título continua sendo o primeiro texto grande que se lê.
   *
   * É a anatomia do detalhe no desenho de 05/09.
   */
  eyebrow?: ReactNode;
  /**
   * A frase abaixo do título — o que a tela mostra, sem repetir o título.
   *
   * É `children` e não uma prop porque quase toda descrição carrega JSX (o
   * nome da base em `<strong>`, um link para a tela vizinha). Como prop, cada
   * conversão viraria um `descricao={<>…</>}`; como filho, o `<p>` que já
   * estava escrito à mão simplesmente vira este componente.
   */
  children?: ReactNode;
  /**
   * O fundo sobre o qual este cabeçalho está desenhado.
   *
   * `chrome` é o bloco tingido — hoje, a saudação dentro do cabeçalho do portal.
   * Ele existe pela terceira vez neste pacote, e sempre pelo mesmo defeito:
   * `Wordmark` e `Metric` decidiam a cor do apoio pelo `size`, que respondia
   * "está sobre fundo escuro?" por acidente enquanto só havia um fundo escuro.
   * Cor de texto se decide pelo fundo REAL, nunca por um vizinho que costuma
   * concordar com ele.
   *
   * Sem isto, a descrição sairia em `texto-suave` dentro do bloco: 1,7:1 contra
   * o laranja, ou seja, ilegível — e nenhuma asserção quebraria, porque texto
   * que some por contraste não quebra teste.
   */
  surface?: 'superficie' | 'chrome';
}) {
  const slot = useContext(PageHeaderSlot);
  /*
   * O `surface` da prop vence o do slot: uma tela que sabe estar sobre um fundo
   * diferente do da moldura precisa poder dizer isso. Sem prop, quem manda é
   * quem ofereceu o lugar.
   */
  const onChrome = (surface ?? slot.surface) === 'chrome';
  const content = (
    <header className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        {back}
        {eyebrow && (
          <div className={cn('flex flex-wrap items-center gap-2', back && 'mt-2')}>
            {eyebrow}
          </div>
        )}
        {aside ? (
          <div className={cn('flex flex-wrap items-center gap-3', back && 'mt-1')}>
            <h1 className="titulo-tela">{title}</h1>
            {aside}
          </div>
        ) : (
          <h1 className={cn('titulo-tela', (back || eyebrow) && 'mt-1.5')}>{title}</h1>
        )}
        {children && (
          <p
            className={cn(
              'apoio-titulo text-sm',
              onChrome ? 'text-chrome-texto-suave' : 'text-texto-suave',
            )}
          >
            {children}
          </p>
        )}
      </div>
      {actions && (
        /*
         * `ml-auto`: quando o cabeçalho quebra — título longo e três ou quatro
         * botões —, as ações caem numa linha só para elas, e sem isto elas
         * encostam na margem esquerda, embaixo do título, lidas como parte
         * dele. Coladas à direita continuam sendo a barra de ações da página.
         * Na mesma linha o `justify-between` já resolve, e o `ml-auto` não muda
         * nada ali.
         */
        <div className="ml-auto flex shrink-0 flex-wrap items-center justify-end gap-2">
          {actions}
        </div>
      )}
    </header>
  );

  return slot.node ? createPortal(content, slot.node) : content;
}
